// DayCloseSection.tsx
import React from 'react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import DayCloseModal from './DayCloseModal';
import { Moon } from 'lucide-react';
import { DayClose } from '../../types/dayClose';

interface DayCloseSectionProps {
  // 外部傳入（Today.tsx 持有狀態時使用）
  isEveningTime?: boolean;
  dayClose?: DayClose | null;
  isLoading?: boolean;
  error?: string | null;
  isModalOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  onRetry?: () => void;
}

const DayCloseSection: React.FC<DayCloseSectionProps> = ({
  isEveningTime,
  dayClose,
  isLoading = false,
  error,
  isModalOpen = false,
  onOpen,
  onClose,
  onRetry,
}) => {
  const isMobile = useMediaQuery('(max-width: 640px)');

  if (!isEveningTime) return null;

  const getCardContent = () => {
    if (!dayClose) {
      return {
        title: 'Time for Day Close',
        description: isMobile
          ? 'End your day mindfully'
          : 'Review today, prepare for tomorrow, and clear your mind',
        buttonText: 'Start',
      };
    }
    if (dayClose.status === 'completed') {
      return {
        title: 'Day Close Completed',
        description: isMobile
          ? "Revisit your day's summary"
          : "You've completed your day close. You can revisit the summary.",
        buttonText: 'Revisit',
      };
    }
    return {
      title: 'Continue Day Close',
      description: isMobile
        ? 'Continue where you left off'
        : 'You have a day close in progress. Continue where you left off.',
      buttonText: 'Continue',
    };
  };

  const { title: cardTitle, description: cardDescription, buttonText } = getCardContent();

  return (
    <div className="night-close-card mb-4 relative overflow-hidden">
      {/* 裝飾星星 */}
      <div className="star-decoration">
        <div className="star absolute top-3 left-5 w-1 h-1 bg-white opacity-60 rounded-full" />
        <div className="star absolute top-6 left-8 w-0.5 h-0.5 bg-white opacity-40 rounded-full" />
        <div className="star absolute top-4 right-12 w-1 h-1 bg-white opacity-70 rounded-full" />
        <div className="star absolute bottom-4 left-14 w-0.5 h-0.5 bg-white opacity-30 rounded-full" />
        <div className="star absolute bottom-6 right-20 w-1 h-1 bg-white opacity-50 rounded-full" />
      </div>

      <div className={`flex ${isMobile ? 'flex-col' : 'items-center justify-between'} relative z-10 p-4`}>
        <div className={`flex items-center ${isMobile ? 'w-full mb-3' : ''}`}>
          <div className="moon-icon-container mr-3 flex-shrink-0">
            <Moon size={isMobile ? 28 : 36} className="text-white opacity-90" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-white">{cardTitle}</h3>
            <p className="text-sm text-gray-300">{cardDescription}</p>
            {error && (
              <p className="text-sm text-red-300 mt-1">
                {error}
                {onRetry && (
                  <button onClick={onRetry} className="ml-2 underline hover:no-underline">
                    Retry
                  </button>
                )}
              </p>
            )}
          </div>
        </div>

        <button
          onClick={onOpen}
          disabled={isLoading || !onOpen}
          className={`${isMobile ? 'w-full mt-6' : 'ml-4'} px-4 py-2 rounded-full border border-purple-400 bg-purple-600 bg-opacity-20 hover:bg-opacity-30 text-white shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isLoading ? 'Loading...' : buttonText}
        </button>
      </div>

      {isModalOpen && dayClose && onClose && (
        <DayCloseModal
          dayCloseId={dayClose.id}
          isOpen={isModalOpen}
          onClose={onClose}
        />
      )}
    </div>
  );
};

export default DayCloseSection;
